import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  Sparkle,
  CheckCircle,
  ArrowRight,
  CreditCard,
  ShieldCheck,
  ArrowLeft,
  Buildings,
  Rocket,
  Brain,
  Lock,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { Footer } from "@/components/Footer";
import api from "@/lib/api";

const PLANS_MONTHLY = [
  {
    id: "pro_monthly",
    type: "Premium",
    price: 19,
    label: "Solo dispatcher",
    subtitle: "Perfect for owner-operators & single-desk dispatch.",
    icon: <Rocket size={22} weight="duotone" />,
    features: [
      "Unlimited load screenings",
      "Side-by-side load comparison",
      "Branded PDF exports",
      "Load history & audit trail",
      "Weekly analytics dashboard",
      "English + Arabic (RTL)",
      "Email support",
    ],
    highlight: true,
    testId: "plan-premium",
  },
  {
    id: "agency_monthly",
    type: "Agency",
    price: 79,
    label: "Agency team",
    subtitle: "For dispatch companies running multi-truck operations.",
    icon: <Buildings size={22} weight="duotone" />,
    features: [
      "Everything in Premium",
      "AI Dispatch Advisor included",
      "Up to 5 dispatcher seats (+$15/mo per extra seat)",
      "Multi-truck cost profiles",
      "Priority email support",
      "Fleet-wide analytics",
      "Custom PDF branding",
    ],
    highlight: false,
    testId: "plan-agency",
  },
];

const PLANS_ANNUAL = [
  {
    id: "pro_annual",
    type: "Premium",
    price: 182, // 19 * 12 * 0.8
    monthlyEquivalent: 15,
    label: "Solo dispatcher",
    subtitle: "Perfect for owner-operators & single-desk dispatch.",
    icon: <Rocket size={22} weight="duotone" />,
    features: PLANS_MONTHLY[0].features,
    highlight: true,
    testId: "plan-premium-annual",
  },
  {
    id: "agency_annual",
    type: "Agency",
    price: 758, // 79 * 12 * 0.8
    monthlyEquivalent: 63,
    label: "Agency team",
    subtitle: "For dispatch companies running multi-truck operations.",
    icon: <Buildings size={22} weight="duotone" />,
    features: PLANS_MONTHLY[1].features,
    highlight: false,
    testId: "plan-agency-annual",
  },
];

const AI_ADDON = {
  id: "ai_addon_monthly",
  type: "AI Pro",
  price: 30,
  label: "AI Dispatch Advisor",
  subtitle: "Claude Sonnet 4.6 reads every load and returns 5 concrete moves to make it profitable.",
  icon: <Brain size={22} weight="duotone" />,
  features: [
    "Rate-negotiation targets in dollars",
    "Fuel surcharge suggestions",
    "Deadhead reduction plays",
    "Weight & commodity leverage",
    "Bilingual output (EN / AR)",
    "Included free with Agency plan",
  ],
  testId: "plan-ai-addon",
};

const PlanCard = ({ plan, cycle, loading, activePlan, onCheckout }) => {
  const isActive = activePlan === plan.type;
  const isThisLoading = loading === plan.id;
  const anyLoading = !!loading;
  const isAnnual = cycle === "annual";
  const displayPrice = isAnnual && plan.monthlyEquivalent ? plan.monthlyEquivalent : plan.price;
  const priceSuffix = isAnnual ? "/ mo, billed annually" : "/ month, per user";

  return (
    <div
      data-testid={plan.testId}
      className={`glass-strong rounded-3xl p-6 md:p-8 relative overflow-hidden flex flex-col ${
        plan.highlight ? "neon-border" : "border-white/10"
      }`}
    >
      {plan.highlight && (
        <div
          aria-hidden
          className="absolute -top-24 -end-24 w-64 h-64 rounded-full blur-3xl opacity-35"
          style={{
            background:
              "radial-gradient(closest-side, rgba(16,185,129,0.55), transparent 70%)",
          }}
        />
      )}
      <div className="relative flex items-start justify-between">
        <div className="flex items-center gap-2 rounded-full bg-emerald-500/12 border border-emerald-500/30 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-emerald-300 font-medium">
          <span className="text-emerald-300">{plan.icon}</span>
          {plan.type}
        </div>
        {isActive && (
          <Badge
            data-testid={`${plan.testId}-active-badge`}
            className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/15 rounded-md text-[10px] uppercase tracking-[0.14em]"
          >
            <Sparkle size={10} weight="fill" className="me-1" />
            Active
          </Badge>
        )}
      </div>

      <div className="relative mt-6">
        <p className="text-white text-lg font-semibold tracking-tight">
          {plan.label}
        </p>
        <p className="text-slate-400 text-sm mt-1">{plan.subtitle}</p>
        <div className="mt-5 flex items-baseline gap-2">
          <span className="tabular text-5xl font-semibold text-white leading-none">
            ${displayPrice}
          </span>
          <span className="text-slate-400 text-sm">{priceSuffix}</span>
        </div>
        {isAnnual && (
          <p className="mt-2 text-[11px] text-emerald-300 tabular">
            ${plan.price} once a year · save 20% vs monthly
          </p>
        )}
      </div>

      <ul className="relative mt-6 space-y-2.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <CheckCircle
              size={16}
              weight="fill"
              className="text-emerald-400 mt-0.5 shrink-0"
            />
            <span className="text-slate-200 text-sm">{f}</span>
          </li>
        ))}
      </ul>

      <div className="relative mt-8">
        {isActive ? (
          <div
            data-testid={`${plan.testId}-active`}
            className="w-full flex items-center gap-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-emerald-200"
          >
            <CheckCircle size={18} weight="fill" />
            <span className="text-sm">Active — enjoy unlimited screenings.</span>
          </div>
        ) : (
          <Button
            data-testid={`btn-checkout-${plan.id}`}
            onClick={() => onCheckout(plan)}
            disabled={anyLoading}
            className={`w-full h-12 rounded-xl text-sm md:text-base ${
              plan.highlight
                ? "btn-neon"
                : "bg-white/[0.06] hover:bg-white/[0.12] text-white border border-white/15"
            }`}
          >
            {isThisLoading ? (
              <>
                <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                Redirecting to Lemon Squeezy…
              </>
            ) : (
              <>
                <CreditCard size={16} weight="duotone" />
                Subscribe · ${displayPrice}/mo
                <ArrowRight size={16} weight="bold" className="flip-x ms-1" />
              </>
            )}
          </Button>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
          <span className="flex items-center gap-1.5">
            <ShieldCheck size={12} weight="duotone" className="text-emerald-400" />
            Secured by Lemon Squeezy
          </span>
          <span>·</span>
          <span>Cancel any time</span>
        </div>
      </div>
    </div>
  );
};

const CheckoutSkeleton = () => (
  <div
    data-testid="checkout-skeleton"
    className="glass rounded-3xl p-8 md:p-10 mt-8 relative overflow-hidden"
  >
    <div className="flex items-center gap-3">
      <div className="w-5 h-5 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
      <p className="text-white font-medium">Verifying your payment…</p>
    </div>
    <p className="mt-2 text-slate-400 text-sm">
      Talking to Lemon Squeezy — this usually takes a few seconds.
    </p>
    <div className="mt-6 space-y-3">
      <div className="h-3 rounded bg-white/8 w-3/4 animate-pulse" />
      <div className="h-3 rounded bg-white/8 w-1/2 animate-pulse" />
      <div className="h-3 rounded bg-white/8 w-2/3 animate-pulse" />
    </div>
  </div>
);

export default function Billing() {
  const { user, isAuthenticated, isSubscribed, planType, refresh } = useAuth();
  const [loading, setLoading] = useState(null); // holds plan_id currently loading
  const [verifying, setVerifying] = useState(false);
  const [cycle, setCycle] = useState("monthly"); // "monthly" | "annual"
  const [params] = useSearchParams();
  const nav = useNavigate();
  const returningSession = params.get("session_id");
  const hasAiAddon = !!user?.has_ai_advisor;
  const plans = cycle === "annual" ? PLANS_ANNUAL : PLANS_MONTHLY;

  useEffect(() => {
    if (!returningSession) return;
    setVerifying(true);
    let attempts = 0;
    const maxAttempts = 10;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/subscription/status/${returningSession}`);
        if (res.data.payment_status === "paid") {
          toast.success("Subscription active — welcome to Pro!");
          await refresh();
          setVerifying(false);
          nav("/app/billing", { replace: true });
          return;
        }
        if (res.data.status === "expired") {
          toast.error("Payment session expired");
          setVerifying(false);
          nav("/app/billing", { replace: true });
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setVerifying(false);
          toast.info("Still processing — refresh in a moment.");
        }
      } catch {
        toast.error("Failed to verify payment");
        setVerifying(false);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returningSession]);

  // Returning from Lemon Squeezy checkout WITHOUT a session_id param
  // (LS redirect_url is static). Poll /auth/me until the webhook lands
  // so the UI flips to Pro automatically.
  const successReturn = window.location.pathname.includes("/billing/success");
  useEffect(() => {
    if (!successReturn || returningSession) return;
    setVerifying(true);
    let attempts = 0;
    const maxAttempts = 10;
    const poll = async () => {
      attempts += 1;
      const fresh = await refresh();
      if (fresh && (fresh.is_subscribed || fresh.has_ai_advisor)) {
        toast.success("Subscription active — welcome to Pro!");
        setVerifying(false);
        nav("/app/billing", { replace: true });
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setVerifying(false);
        toast.info("Payment received — your plan will activate within a minute.");
        nav("/app/billing", { replace: true });
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (plan) => {
    if (!isAuthenticated) {
      nav("/login", { state: { from: "/app/billing" } });
      return;
    }
    setLoading(plan.id);
    try {
      const res = await api.post("/checkout/session", {
        plan_id: plan.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to start checkout";
      toast.error(typeof msg === "string" ? msg : "Checkout error");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a192f] text-white landing-root">
      <div className="ticker-line" />

      <header className="max-w-[1200px] mx-auto px-5 md:px-8 py-5 flex items-center justify-between">
        <Link
          to="/app"
          className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" className="flip-x" />
          Back to calculator
        </Link>
        {user && (
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 hidden md:inline">
              {user.email}
            </span>
            {isSubscribed ? (
              <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-md text-[10px] uppercase tracking-[0.14em]">
                <Sparkle size={10} weight="fill" className="me-1" />
                {planType || "Pro"} active
              </Badge>
            ) : (
              <Badge className="bg-white/8 text-slate-300 border border-white/15 rounded-md text-[10px] uppercase tracking-[0.14em]">
                Free
              </Badge>
            )}
          </div>
        )}
      </header>

      <main className="max-w-[1200px] mx-auto px-5 md:px-8 py-8 md:py-14">
        <div className="text-center">
          <div className="kicker mx-auto">
            <span className="dot" />
            Billing
          </div>
          <h1 className="mt-5 text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
            Unlock the full{" "}
            <span className="italic neon-text">calculator.</span>
          </h1>
          <p className="mt-4 text-slate-400 text-base md:text-lg max-w-lg mx-auto">
            Two plans. One card charge. Cancel any time. Powered by Lemon Squeezy.
          </p>
          <div className="mt-4 flex justify-center flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 tabular">
            <span>Visa</span>
            <span>·</span>
            <span>Mastercard</span>
            <span>·</span>
            <span>American Express</span>
            <span>·</span>
            <span>Apple Pay</span>
            <span>·</span>
            <span>Google Pay</span>
          </div>
        </div>

        {verifying && <CheckoutSkeleton />}

        {/* Monthly / Annual toggle */}
        <div className="mt-10 flex justify-center">
          <div
            role="tablist"
            aria-label="Billing cycle"
            className="inline-flex items-center gap-1 rounded-full p-1 border border-white/10 bg-white/[0.03]"
          >
            <button
              role="tab"
              aria-selected={cycle === "monthly"}
              data-testid="billing-toggle-monthly"
              onClick={() => setCycle("monthly")}
              className={`relative px-5 py-2 rounded-full text-xs md:text-sm font-medium transition-colors ${
                cycle === "monthly"
                  ? "bg-white text-[#0a192f]"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              role="tab"
              aria-selected={cycle === "annual"}
              data-testid="billing-toggle-annual"
              onClick={() => setCycle("annual")}
              className={`relative px-5 py-2 rounded-full text-xs md:text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                cycle === "annual"
                  ? "bg-white text-[#0a192f]"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              Annual
              <span
                data-testid="save-20-badge"
                className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                  cycle === "annual"
                    ? "bg-emerald-500/20 text-emerald-700 border border-emerald-500/40"
                    : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                }`}
              >
                Save 20%
              </span>
            </button>
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-5 md:gap-6">
          {plans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              cycle={cycle}
              loading={loading}
              activePlan={isSubscribed ? planType : null}
              onCheckout={startCheckout}
            />
          ))}
        </div>

        {/* AI Pro Add-on — separate section */}
        <div className="mt-8">
          <div className="text-center mb-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-medium">
              Optional add-on
            </p>
            <p className="text-slate-400 text-sm mt-1">
              Any plan · plug the AI Advisor in for +$30/mo
            </p>
          </div>
          <div
            data-testid={AI_ADDON.testId}
            className="glass-strong rounded-3xl p-6 md:p-8 relative overflow-hidden border-white/10"
          >
            <div
              aria-hidden
              className="absolute -top-24 -end-24 w-64 h-64 rounded-full blur-3xl opacity-30"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(16,185,129,0.5), transparent 70%)",
              }}
            />
            <div className="relative grid md:grid-cols-2 gap-6 md:gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/12 border border-emerald-500/30 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-emerald-300 font-medium">
                  <span className="text-emerald-300">{AI_ADDON.icon}</span>
                  {AI_ADDON.type}
                </div>
                <p className="mt-5 text-white text-2xl md:text-3xl font-semibold tracking-tight">
                  {AI_ADDON.label}
                </p>
                <p className="mt-2 text-slate-400 text-sm max-w-md">
                  {AI_ADDON.subtitle}
                </p>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="tabular text-5xl font-semibold text-white leading-none">
                    +$30
                  </span>
                  <span className="text-slate-400 text-sm">/ month add-on</span>
                </div>

                <div className="mt-6">
                  {hasAiAddon ? (
                    <div
                      data-testid={`${AI_ADDON.testId}-active`}
                      className="inline-flex items-center gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-4 py-2.5 text-emerald-200 text-sm"
                    >
                      <CheckCircle size={16} weight="fill" />
                      AI Advisor active
                    </div>
                  ) : (
                    <Button
                      data-testid={`btn-checkout-${AI_ADDON.id}`}
                      onClick={() => startCheckout(AI_ADDON)}
                      disabled={!!loading}
                      className="btn-neon h-11 rounded-xl text-sm"
                    >
                      {loading === AI_ADDON.id ? (
                        <>
                          <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                          Redirecting to Lemon Squeezy…
                        </>
                      ) : (
                        <>
                          <Sparkle size={14} weight="fill" />
                          Add AI Advisor · $30/mo
                          <ArrowRight size={14} weight="bold" className="flip-x ms-1" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              <ul className="space-y-2.5">
                {AI_ADDON.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <CheckCircle
                      size={16}
                      weight="fill"
                      className="text-emerald-400 mt-0.5 shrink-0"
                    />
                    <span className="text-slate-200 text-sm">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* Trust badges */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-8 pb-12">
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { icon: <ShieldCheck size={18} weight="duotone" />, label: "Secured by Lemon Squeezy", sub: "PCI DSS Level 1 certified" },
            { icon: <Lock size={18} weight="duotone" />, label: "256-bit SSL Encrypted", sub: "Bank-grade TLS in transit" },
            { icon: <ArrowRight size={18} weight="duotone" className="flip-x" />, label: "Cancel anytime", sub: "7-day money-back guarantee" },
          ].map((b) => (
            <div
              key={b.label}
              data-testid={`trust-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="glass rounded-xl p-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/25 grid place-items-center text-emerald-300">
                {b.icon}
              </div>
              <div>
                <p className="text-white text-sm font-semibold">{b.label}</p>
                <p className="text-slate-500 text-xs">{b.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {isAuthenticated && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 glass rounded-xl p-4">
            <div>
              <p className="text-white text-sm font-semibold">Manage your subscription</p>
              <p className="text-slate-500 text-xs">Update card, download invoices, or cancel from Lemon Squeezy's portal.</p>
            </div>
            <Button
              data-testid="btn-customer-portal"
              onClick={async () => {
                try {
                  const res = await api.post("/create-customer-portal-session", {
                    return_url: window.location.origin + "/app/billing",
                  });
                  if (res.data.url) window.location.href = res.data.url;
                } catch {
                  toast.error("Portal unavailable — contact support@loadwise.app");
                }
              }}
              variant="outline"
              className="rounded-lg border-white/15 text-slate-200 bg-white/[0.04] hover:bg-white/[0.08] h-10"
            >
              Manage subscription
              <ArrowRight size={14} weight="bold" className="flip-x ms-1.5" />
            </Button>
          </div>
        )}
      </section>

      <Footer />
      <Toaster position="top-right" richColors closeButton theme="dark" />
    </div>
  );
}
